import { useState } from 'react'

/** Controlled explorer link: `<storageId>:<path+search>`, or a bare `<path+search>` without storages. */
export interface TelLink {
  value?: string
  onChange: (next: string) => void
}

/** Reads one `key=value` pair from the document URL hash. */
export function readHashParam(key: string): string | undefined {
  return new URLSearchParams(location.hash.slice(1)).get(key) ?? undefined
}

/**
 * Sets or removes (undefined) hash keys with `history.replaceState`: no history entry; pathname,
 * search, `history.state` and every other `&` segment of the hash stay byte-exact. A changed key's
 * old segments are dropped and its new value appended, encoded.
 */
export function writeHashParams(entries: Record<string, string | undefined>): void {
  const kept = location.hash.slice(1).split('&').filter(segment =>
    segment !== '' && !Object.hasOwn(entries, new URLSearchParams(segment).keys().next().value ?? ''))
  const owned = Object.entries(entries).flatMap(([key, value]) =>
    value === undefined ? [] : [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`])
  const url = new URL(location.href)
  url.hash = [...kept, ...owned].join('&')
  // Browsers fire a `navigate` event even for an unchanged URL; skip writes that change nothing.
  if (url.href !== location.href) history.replaceState(history.state, '', url)
}

/** Explorer link kept in the URL hash key `tel`, so a reload or shared URL reopens the same view. */
export function useTelHashLink(): TelLink {
  const [value] = useState(() => readHashParam('tel'))
  return {
    value,
    onChange: (next) => {
      writeHashParams({ tel: next })
    },
  }
}
